import * as THREE from 'three';

// WHAT YOU CANNOT WALK THROUGH, INDOORS (2026-10-31).
//
// Khari: "you can walk through the bar and step onto chairs." Nothing indoors
// was ever solid: FloorClamp asks one question — is there floor under you — and
// its probe starts 0.35u above your feet, so a chair seat (0.21u) is floor you
// step onto, and a table or bar top (0.35u, 0.48u) is above the probe, which
// passes underneath and finds the floor again. Nothing looked sideways.
//
// The rooms already say what their furniture is. A kit-built room (make_jahva,
// make_salon) keeps every affordance-bearing node whole with its tag in
// userData — chairs, tables, the bar's tops, the couch — while the rest is
// merged by material. So solidity is READ from the model, not described: each
// solid node becomes a footprint in ROOM SPACE (MeshInterior's rigid anchor), a
// circle when it is round or square (a table top, a stool), else a box turned
// with the node. A downloaded room has no tags, and its meshes cannot be
// trusted to be the thing they are named for (the Corner Market's counter mesh
// carries floor-level junk 6.7m long), so rooms.js gives it boxes instead —
// `solid: { boxes: [{ name, x: [x0, x1], z: [z0, z1], y: [y0, y1] }] }`, room
// space — and `solid: false` opts a room out.
//
// Footprints, not triangles, on purpose: stairs, ramps and the spoken-word
// platform stay walkable without anyone describing them (a riser is never
// solid), a footprint can always push you OUT (a mesh can trap you inside it),
// and the cost is a handful of circle tests a frame.
//
// Each footprint keeps its height range, so it only applies on its own level:
// the café's tables are not in the way upstairs, the loft's couch is not in the
// way downstairs.

// Affordances that stand on the floor and take up room. Groups that merely
// collect (a salon `station`, a `basin`, the `bar`) are walked into, so their
// own solid parts are found one by one — the barista's aisle stays an aisle.
const SOLID = new Set(['furniture', 'surface', 'reception', 'workstation', 'display', 'basin_bowl']);
const ROUND = 1.15;     // a footprint this close to square is a circle

const tmpBox = new THREE.Box3();
const tmpM = new THREE.Matrix4();
const inv = new THREE.Matrix4();
const rel = new THREE.Matrix4();
const v = new THREE.Vector3();

/** The node's geometry, boxed in the node's own frame (unscaled, unturned). */
function localBox(node) {
  const box = new THREE.Box3();
  const nodeInv = tmpM.copy(node.matrixWorld).invert();
  node.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    tmpBox.copy(o.geometry.boundingBox).applyMatrix4(rel.multiplyMatrices(nodeInv, o.matrixWorld));
    box.union(tmpBox);
  });
  return box.isEmpty() ? null : box;
}

/** One node → its footprint in room space: { cx, cz, r } or { cx, cz, ux, uz, hu, hv }, plus y0/y1. */
function footprintOf(node, roomInv, name) {
  const box = localBox(node);
  if (!box) return null;
  rel.multiplyMatrices(roomInv, node.matrixWorld);
  const e = rel.elements;
  // The node's own x and z axes, laid flat in room space (rooms only ever turn
  // furniture about the vertical).
  let ux = e[0], uz = e[2];
  const ul = Math.hypot(ux, uz) || 1; ux /= ul; uz /= ul;
  const vx = -uz, vz = ux;
  const pts = [];
  for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
    pts.push(v.set(x, y, z).applyMatrix4(rel).clone());
  }
  let y0 = Infinity, y1 = -Infinity, cx = 0, cz = 0;
  for (const p of pts) { y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); cx += p.x / 8; cz += p.z / 8; }
  let hu = 0, hv = 0;
  for (const p of pts) {
    const dx = p.x - cx; const dz = p.z - cz;
    hu = Math.max(hu, Math.abs(dx * ux + dz * uz));
    hv = Math.max(hv, Math.abs(dx * vx + dz * vz));
  }
  if (hu < 0.004 && hv < 0.004) return null;
  const big = Math.max(hu, hv); const small = Math.max(Math.min(hu, hv), 1e-6);
  const base = { name, cx, cz, y0, y1 };
  return big / small <= ROUND ? { ...base, r: big } : { ...base, ux, uz, hu, hv };
}

/** Every solid footprint in a fitted room model, in the space of `space` (the room's anchor). */
export function buildSolids(model, space, room) {
  if (!model || !space || room?.solid === false) return [];
  model.updateWorldMatrix(true, true);
  inv.copy(space.matrixWorld).invert();
  const out = [];
  const visit = (node) => {
    const a = node.userData?.affordance;
    if (a && SOLID.has(a)) {
      const f = footprintOf(node, inv, node.name || a);
      if (f) out.push(f);
      return;   // a solid's parts are inside its footprint already
    }
    for (const c of node.children) visit(c);
  };
  visit(model);
  // Authored boxes, room space, for a model with no tags (rooms.js `solid.boxes`).
  for (const b of room?.solid?.boxes || []) {
    const [x0, x1] = b.x; const [z0, z1] = b.z; const [y0, y1] = b.y;
    out.push({ name: b.name || 'box', cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, ux: 1, uz: 0, hu: Math.abs(x1 - x0) / 2, hv: Math.abs(z1 - z0) / 2, y0, y1 });
  }
  return out;
}

/** The transform from model to room — when it changes (a refit, the placement slider) the footprints are rebuilt. */
export function fitKey(model, space) {
  rel.multiplyMatrices(tmpM.copy(space.matrixWorld).invert(), model.matrixWorld);
  return rel.elements.map((n) => n.toFixed(4)).join(',');
}

/**
 * Push a body of radius `R` at room-space (p.x, p.z), feet at `feet`, out of
 * every solid on its level. Moves `p` in place; true if anything moved it.
 * A footprint applies when it rises above your feet and starts below your
 * chest — not the floor below a balcony, not a shelf over your head.
 */
export function pushOut(p, feet, solids, R, { headroom = 0.9 } = {}) {
  let moved = false;
  for (let pass = 0; pass < 3; pass++) {
    let any = false;
    for (const s of solids) {
      if (s.y1 <= feet + 0.05 || s.y0 >= feet + headroom) continue;
      if (s.r != null) {
        const dx = p.x - s.cx; const dz = p.z - s.cz;
        const d = Math.hypot(dx, dz); const min = s.r + R;
        if (d >= min) continue;
        // Dead centre (arrived inside it): out along +x, any way is out.
        const k = d > 1e-6 ? min / d : 0;
        p.x = s.cx + (d > 1e-6 ? dx * k : min);
        p.z = s.cz + (d > 1e-6 ? dz * k : 0);
      } else {
        const vx = -s.uz; const vz = s.ux;
        const dx = p.x - s.cx; const dz = p.z - s.cz;
        let lu = dx * s.ux + dz * s.uz;
        let lv = dx * vx + dz * vz;
        const qu = Math.max(-s.hu, Math.min(s.hu, lu));
        const qv = Math.max(-s.hv, Math.min(s.hv, lv));
        const eu = lu - qu; const ev = lv - qv;
        const d2 = eu * eu + ev * ev;
        if (d2 >= R * R) continue;
        if (d2 > 1e-10) {
          const d = Math.sqrt(d2); const k = (R - d) / d;
          lu += eu * k; lv += ev * k;
        } else if (s.hu - Math.abs(lu) < s.hv - Math.abs(lv)) {
          lu = (lu < 0 ? -1 : 1) * (s.hu + R);        // inside: out through the nearer face
        } else {
          lv = (lv < 0 ? -1 : 1) * (s.hv + R);
        }
        p.x = s.cx + s.ux * lu + vx * lv;
        p.z = s.cz + s.uz * lu + vz * lv;
      }
      any = true; moved = true;
    }
    if (!any) break;
  }
  return moved;
}
